import React, { useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faCheck,
    faTimes,
    faShoppingCart,
    faTruck,
    faSpinner,
    faUserPlus,
    faFileAlt,
    faBoxOpen,
    faEnvelope,
    faComment,
    faHistory,
    faPrint,
    faBan,
} from '@fortawesome/free-solid-svg-icons';
import { USER_ROLES, AUDIT_LOG_VIEWER_ROLES } from '../../utils/constants';
import { canEditChallanRecord, editDeniedReason } from '../../utils/challanRbac';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';
import { useMessageAlert } from '../common/MessageAlert';
import ConfirmDialog from '../common/ConfirmDialog';
import AuditLogsModal from '../common/AuditLogsModal';
import { useChannelSettings } from '../../context/ChannelSettingsContext';

const OrderStatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = (status || '').toString().toLowerCase();

    const getLabel = (key) => {
        if (!key) return '';
        const k = key.toLowerCase();
        if (k === 'supervisor_assigned') return 'Sup. Assgn.';
        return t(`deliveryOrdersModule.actions.status.${k}`) || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const badgeStyle = (icon, color, label) => (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={icon} className="text-sm" style={{ color }} />
            <span className="text-sm font-medium" style={{ color }}>{label}</span>
        </div>
    );

    if (['approved', 'completed', 'delivered'].includes(s)) return badgeStyle(faCheck, UI_COLORS.STATUS.SUCCESS, getLabel(s));
    if (['rejected', 'cancelled', 'failed'].includes(s)) return badgeStyle(faTimes, UI_COLORS.STATUS.ERROR, getLabel(s));
    if (s === 'ready_for_challan') return badgeStyle(faFileAlt, '#0d9488', getLabel(s));
    if (['in_transit', 'delivery_assigned', 'loaded', 'supervisor_assigned'].includes(s)) return badgeStyle(faTruck, UI_COLORS.STATUS.INFO, getLabel(s));

    // Check for loading states
    if (s.includes('loading')) {
        // Special case for 'loading' status which might match 'loading_started' etc if we had them, 
        // but simple 'loading' is covered.
        return badgeStyle(faSpinner, UI_COLORS.STATUS.INFO, getLabel('loading'));
    }

    return badgeStyle(faShoppingCart, UI_COLORS.STATUS.WARNING, getLabel(s));
};

const DeliveryOrdersTable = ({
    orders = [],
    loading = false,
    onViewOrder,
    onAssignSupervisor,
    onStartLoading,
    onCompleteLoading,
    onGenerateChallan,
    onPrintChallan,
    onMarkDelivered,
    onEditTruckWeight,
    onSendChallanNotification,
    onRefresh,
    userRole,
    enablePagination = true,
}) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useMessageAlert();
    const { emailEnabled, whatsappEnabled } = useChannelSettings();
    const [actionLoading, setActionLoading] = useState(false);
    const [auditLogTarget, setAuditLogTarget] = useState(null);
    const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
    const isAdminOrSuperAdmin = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(userRole);
    const canGenerateChallan = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole);
    const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
    const canCancelDO = userRole === USER_ROLES.SUPER_ADMIN;

    // Shared rule (src/utils/challanRbac.js). A delivery order stays LOADED after its
    // challan is raised, so anything that edits challan content past that point is
    // Super Admin only, and only on the challan's own IST day.
    const challanEditState = useCallback((o) => ({
        userRole,
        challanGenerated: !!o?.challan_generated,
        challanDate: o?.challan_date,
    }), [userRole]);
    const canEditChallanContent = useCallback(
        (o) => canEditChallanRecord(challanEditState(o)), [challanEditState]);
    const challanEditReason = useCallback(
        (o) => editDeniedReason(challanEditState(o)) || null, [challanEditState]);

    // Confirm Dialog State for Cancel DO
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelTargetId, setCancelTargetId] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    // Confirm Dialog State for Mark Delivered
    const [showConfirmDeliver, setShowConfirmDeliver] = useState(false);
    const [confirmId, setConfirmId] = useState(null);

    // Confirm Dialog State for Send Challan Notification
    const [showSendChallanEmailConfirm, setShowSendChallanEmailConfirm] = useState(false);
    const [sendChallanEmailTargetId, setSendChallanEmailTargetId] = useState(null);
    const [pendingChannel, setPendingChannel] = useState('email');

    const openSendDialog = useCallback((id, channel) => {
        setPendingChannel(channel);
        setSendChallanEmailTargetId(id);
        setShowSendChallanEmailConfirm(true);
    }, []);

    const handlePrintGenericDeliveryOrder = useCallback(async (orderOrId) => {
        const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
        if (!id) return;
        try {
            const { default: deliveryOrderService } = await import('../../services/deliveryOrderService');
            const res = await deliveryOrderService.getDeliverySubOrderById(id);
            if (res && res.success && res.data) {
                import('../../utils/printHelpers').then(({ printDocument }) => {
                    printDocument(res.data, 'Delivery Order');
                });
            } else {
                showError(t('common.messages.failedToLoadDeliveryOrderForPrinting'));
            }
        } catch(e) {
            showError(t('common.messages.failedToLoadDeliveryOrderForPrinting'));
        }
    }, [showError]);

    const handleAction = useCallback(async (actionFn, id, successMsg, failMsg) => {
        setActionLoading(true);
        try {
            const res = await actionFn(id, {});
            if (res && res.success) {
                showSuccess(successMsg);
                if (onRefresh) onRefresh();
            } else {
                showError(failMsg + (res?.error ? `: ${res.error}` : ''));
            }
        } catch (e) {
            showError(failMsg);
        } finally {
            setActionLoading(false);
        }
    }, [showSuccess, showError, onRefresh]);

    const columns = useMemo(() => [
        {
            id: 'sub_order_number',
            label: t('deliveryOrdersModule.table.columns.subOrderNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.sub_order_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.sub_order_number || '-'}
                </div>
            ),
        },
        {
            id: 'order_number',
            label: t('deliveryOrdersModule.table.columns.orderNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.order_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm text-gray-600">
                    {o.order_number || '-'}
                </div>
            ),
        },
        {
            id: 'tally_name',
            label: t('deliveryOrdersModule.table.columns.tallyName'),
            sortable: true,
            sortAccessor: (o) => (o?.tally_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.tally_name || o.customer_name || '-'}
                </div>
            ),
        },
        {
            id: 'location_name',
            label: t('deliveryOrdersModule.table.columns.location'),
            sortable: true,
            sortAccessor: (o) => (o?.location_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {o.location_name || '-'}
                </div>
            ),
        },
        {
            id: 'vehicle_number',
            label: t('commonCols.vehicleNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.vehicle_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.vehicle_number || '-'}
                </div>
            ),
        },
        {
            id: 'total_quantity',
            label: t('deliveryOrdersModule.table.columns.totalQty'),
            sortable: true,
            sortAccessor: (o) => o?.total_delivered_quantity || o?.total_quantity || 0,
            renderCell: (o) => (
                <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.total_delivered_quantity || o.total_quantity} {o.unit_type}
                </span>
            ),
        },
        {
            id: 'order_remark',
            label: t('commonCols.orderRemarks'),
            sortable: true,
            sortAccessor: (o) => (o?.order_remark || '').toLowerCase(),
            renderCell: (o) => {
                if (!o.order_remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>-</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={o.order_remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {o.order_remark}
                    </div>
                );
            },
        },
        {
            id: 'status',
            label: t('deliveryOrdersModule.table.columns.status'),
            sortable: true,
            align: 'center',
            renderCell: (o) => {
                const statusStr = (o?.status || '').toLowerCase();
                return (
                    <div className="flex flex-col items-center gap-1">
                        <OrderStatusBadge status={o.status} />
                        {statusStr === 'supervisor_assigned' && !isSupervisor && o.supervisor_name && (
                            <span
                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{
                                    backgroundColor: 'rgba(234, 179, 8, 0.15)',
                                    color: '#92400e',
                                    border: '1px solid rgba(234, 179, 8, 0.4)',
                                }}
                            >
                                {o.supervisor_name.trim()}
                            </span>
                        )}
                        {statusStr === 'supervisor_assigned' && isSupervisor && o.assigned_by_name && (
                            <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{
                                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                    color: '#4338ca',
                                    border: '1px solid rgba(99, 102, 241, 0.2)',
                                }}
                            >
                                By: {o.assigned_by_name.trim()}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'status_actions',
            label: t('commonCols.updateStatus'),
            align: 'center',
            renderCell: (o) => {
                const statusStr = (o?.status || '').toLowerCase();

                return (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {/* Direct flow: Generate Challan directly from ready_for_challan */}
                        {statusStr === 'ready_for_challan' && canGenerateChallan && (
                            <button
                                onClick={() => onGenerateChallan && onGenerateChallan(o.id)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: '#0d9488', backgroundColor: 'rgba(13, 148, 136, 0.08)' }}
                                title={t('deliveryOrdersModule.actions.generateChallan')}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.generateChallan')}</span>
                            </button>
                        )}

                        {/* 1. Assign Supervisor (PENDING -> SUPERVISOR_ASSIGNED) — warehouse flow only */}
                        {statusStr === 'pending' && !isSupervisor && !o.is_direct_flow && (
                            <button
                                onClick={() => onAssignSupervisor && onAssignSupervisor(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                                title={t('deliveryOrdersModule.actions.assignSupervisor')}
                            >
                                <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.assignSupervisor')}</span>
                            </button>
                        )}

                        {/* 2. Start Loading (SUPERVISOR_ASSIGNED -> LOADING) */}
                        {statusStr === 'supervisor_assigned' && (
                            <button
                                onClick={() => onStartLoading && onStartLoading(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                title={t('deliveryOrdersModule.actions.startLoading')}
                            >
                                <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.startLoading')}</span>
                            </button>
                        )}

                        {/* 3. Complete Loading (LOADING -> LOADED) */}
                        {statusStr === 'loading' && (
                            <button
                                onClick={() => onCompleteLoading && onCompleteLoading(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                title={t('deliveryOrdersModule.actions.completeLoading')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.completeLoading')}</span>
                            </button>
                        )}

                        {/* Edit Supervisor (SUPERVISOR_ASSIGNED, LOADING) */}
                        {['supervisor_assigned', 'loading'].includes(statusStr) && (
                            <button
                                onClick={() => onAssignSupervisor && onAssignSupervisor(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                                title={t('deliveryOrdersModule.actions.editSupervisor')}
                            >
                                <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.editSupervisor')}</span>
                            </button>
                        )}

                        {/* 4. Generate/Print Challan */}
                        {statusStr === 'loaded' && !o.challan_generated && canGenerateChallan && (
                            <button
                                onClick={() => onGenerateChallan && onGenerateChallan(o.id)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                                title={t('deliveryOrdersModule.actions.generateChallan')}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.generateChallan')}</span>
                            </button>
                        )}

                        {/* Edit Truck Weight (Only in LOADED state). Truck weights print
                            on the challan, so once one exists this follows the challan
                            edit rule rather than staying open to every role. */}
                        {statusStr === 'loaded' && (
                            <button
                                onClick={() => {
                                    if (!canEditChallanContent(o)) return;
                                    onEditTruckWeight && onEditTruckWeight(o);
                                }}
                                disabled={actionLoading || !canEditChallanContent(o)}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08) }}
                                title={challanEditReason(o) || t('deliveryOrdersModule.actions.editTruckWeight')}
                            >
                                <FontAwesomeIcon icon={faTruck} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.editTruckWeight')}</span>
                            </button>
                        )}

                        {o.challan_generated && !isSupervisor && (isAdminOrSuperAdmin || !o.challan_printed) && (
                            <button
                                onClick={() => onPrintChallan && onPrintChallan(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                                title={t('common.actions.printChallan')}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                                <span className="text-xs font-medium">Print Challan</span>
                            </button>
                        )}

                        {/* Send Challan Notification - only when challan is generated */}
                        {o.challan_generated && !isSupervisor && emailEnabled && (
                            <button
                                onClick={() => openSendDialog(o.id, 'email')}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                                title={t('common.actions.sendChallanEmail')}
                            >
                                <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                                <span className="text-xs font-medium">Email</span>
                            </button>
                        )}
                        {o.challan_generated && !isSupervisor && whatsappEnabled && (
                            <button
                                onClick={() => openSendDialog(o.id, 'whatsapp')}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ color: '#ffffff', backgroundColor: '#25D366' }}
                                title={t('common.actions.sendChallanWhatsApp')}
                            >
                                <FontAwesomeIcon icon={faComment} className="text-xs" />
                                <span className="text-xs font-medium">WhatsApp</span>
                            </button>
                        )}

                        {/* "Mark In Transit" was removed: generating the challan already
                            moves the sub-order to IN_TRANSIT, so LOADED + challan never
                            occurred and the button was dead. */}

                        {/* 6. Mark Delivered (IN_TRANSIT -> DELIVERED) */}
                        {statusStr === 'in_transit' && !isSupervisor && (
                            <button
                                onClick={() => {
                                    setConfirmId(o.id);
                                    setShowConfirmDeliver(true);
                                }}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                title={t('deliveryOrdersModule.actions.markDelivered')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.markDelivered')}</span>
                            </button>
                        )}

                        {/* Cancel DO — eligible roles, not delivered/cancelled. Post-challan only for super-admin. */}
                        {canCancelDO && !['delivered', 'cancelled'].includes(statusStr) && (() => {
                            const blockedPostChallan = o.challan_generated && !isSuperAdmin;
                            return (
                                <button
                                    onClick={() => {
                                        if (blockedPostChallan) return;
                                        setCancelTargetId(o.id);
                                        setCancelReason('');
                                        setShowCancelConfirm(true);
                                    }}
                                    disabled={actionLoading || blockedPostChallan}
                                    className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08) }}
                                    title={blockedPostChallan
                                        ? t('deliveryOrdersModule.actions.cancelDOSuperAdminOnly')
                                        : t('deliveryOrdersModule.actions.cancelDO')}
                                >
                                    <FontAwesomeIcon icon={faBan} className="text-xs" />
                                    <span className="text-xs font-medium">{t('deliveryOrdersModule.actions.cancelDO')}</span>
                                </button>
                            );
                        })()}

                    </div>
                );
            }
        },
        {
            id: 'actions',
            label: t('deliveryOrdersModule.table.columns.actions'),
            align: 'right',
            renderCell: (o) => (
                <div className="flex items-center justify-end space-x-2">
                    <button
                        onClick={() => onViewOrder && onViewOrder(o)}
                        className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                        title={t('deliveryOrdersModule.table.actions.view')}
                        style={{ color: UI_COLORS.TEXT.PRIMARY }}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>

                    {(isAdminOrSuperAdmin || !o.challan_printed) && (
                        <button
                            onClick={() => handlePrintGenericDeliveryOrder(o.id)}
                            className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                            title={t('common.actions.printDeliveryOrder')}
                            style={{ color: UI_COLORS.TEXT.PRIMARY }}
                        >
                            <FontAwesomeIcon icon={faPrint} />
                        </button>
                    )}

                    {AUDIT_LOG_VIEWER_ROLES.includes(userRole) && (
                        <button
                            onClick={() => setAuditLogTarget({ id: o.id, label: o.sub_order_number })}
                            className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                            title={t('common.actions.auditLogs')}
                            style={{ color: UI_COLORS.STATUS.INFO }}
                        >
                            <FontAwesomeIcon icon={faHistory} />
                        </button>
                    )}
                </div>
            ),
        },
    ], [onViewOrder, actionLoading, onAssignSupervisor, onStartLoading, onCompleteLoading, onGenerateChallan, onPrintChallan, onEditTruckWeight, handleAction, userRole, isSupervisor, isAdminOrSuperAdmin, canGenerateChallan, canCancelDO, isSuperAdmin, canEditChallanContent, challanEditReason, setAuditLogTarget, handlePrintGenericDeliveryOrder, emailEnabled, whatsappEnabled, openSendDialog]);

    return (
        <>
            <DataTable
                columns={columns}
                data={orders}
                loading={loading || actionLoading}
                keyField="id"
                enablePagination={enablePagination}
                itemsPerPage={25}
                defaultSort={{ field: 'updated_at', direction: 'desc' }}
                emptyState={{
                    title: t('deliveryOrdersModule.table.empty.title'),
                    message: t('deliveryOrdersModule.table.empty.message'),
                }}
            />

            <AuditLogsModal
                isOpen={!!auditLogTarget}
                onClose={() => setAuditLogTarget(null)}
                entityType="delivery_sub_order"
                entityId={auditLogTarget?.id}
                entityLabel={auditLogTarget?.label}
                hasTimeline={false}
            />

            <ConfirmDialog
                isOpen={showConfirmDeliver}
                title={t('deliveryOrdersModule.actions.markDelivered')}
                message={t('deliveryOrdersModule.actions.confirmMarkDelivered')}
                confirmLabel="Yes, Delivered"
                confirmVariant="success"
                loading={actionLoading}
                onCancel={() => setShowConfirmDeliver(false)}
                onConfirm={() => {
                    setShowConfirmDeliver(false);
                    if (confirmId) {
                        handleAction(
                            onMarkDelivered,
                            confirmId,
                            t('deliveryOrdersModule.actions.markDeliveredSuccess'),
                            t('deliveryOrdersModule.actions.markDeliveredFailed')
                        );
                    }
                }}
            />

            <ConfirmDialog
                isOpen={showCancelConfirm}
                title={t('deliveryOrdersModule.actions.cancelDO')}
                message={(
                    <div>
                        <p style={{ marginBottom: 12 }}>{t('deliveryOrdersModule.actions.cancelDOConfirm')}</p>
                        {(() => {
                            // Money already received against this challan stays on record as
                            // unapplied after a cancel (policy: recompute + warn, allow).
                            const target = orders.find((o) => String(o.id) === String(cancelTargetId));
                            const paid = Number(target?.amount_paid || 0);
                            return paid > 0 ? (
                                <p style={{ marginBottom: 12, color: UI_COLORS.STATUS?.ERROR || '#dc2626', fontWeight: 600 }}>
                                    {t('deliveryOrdersModule.actions.cancelDOPaidWarning', {
                                        amount: paid.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                                    })}
                                </p>
                            ) : null;
                        })()}
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder={t('deliveryOrdersModule.actions.cancelDOReasonPlaceholder')}
                            rows={3}
                            style={{
                                width: '100%', padding: '10px 12px', borderRadius: 10,
                                border: `1px solid ${UI_COLORS.BORDERS?.LIGHT || '#e5e7eb'}`,
                                fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                            }}
                        />
                    </div>
                )}
                confirmLabel={t('deliveryOrdersModule.actions.cancelDO')}
                confirmVariant="danger"
                loading={actionLoading}
                onCancel={() => { setShowCancelConfirm(false); setCancelTargetId(null); setCancelReason(''); }}
                onConfirm={async () => {
                    if (!cancelTargetId) return;
                    setActionLoading(true);
                    try {
                        const { default: deliveryOrderService } = await import('../../services/deliveryOrderService');
                        const res = await deliveryOrderService.cancelSubOrder(cancelTargetId, cancelReason.trim() || undefined);
                        if (res && res.success) {
                            showSuccess(t('deliveryOrdersModule.actions.cancelDOSuccess'));
                            if (onRefresh) onRefresh();
                        } else {
                            showError(res?.error || t('deliveryOrdersModule.actions.cancelDOFailed'));
                        }
                    } catch {
                        showError(t('deliveryOrdersModule.actions.cancelDOFailed'));
                    } finally {
                        setActionLoading(false);
                        setShowCancelConfirm(false);
                        setCancelTargetId(null);
                        setCancelReason('');
                    }
                }}
            />

            <ConfirmDialog
                isOpen={showSendChallanEmailConfirm}
                title={`Send Challan via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
                message={t('deliveryOrdersModule.actions.sendChallanEmailConfirm')}
                confirmLabel={t('deliveryOrdersModule.common.buttons.sendChallanEmail')}
                loadingLabel={t('deliveryOrdersModule.common.buttons.sendingEmail')}
                confirmVariant="primary"
                loading={actionLoading}
                onCancel={() => setShowSendChallanEmailConfirm(false)}
                onConfirm={async () => {
                    setShowSendChallanEmailConfirm(false);
                    if (sendChallanEmailTargetId) {
                        setActionLoading(true);
                        try {
                            const res = await onSendChallanNotification(sendChallanEmailTargetId, pendingChannel);
                            if (res && res.success) {
                                showSuccess(pendingChannel === 'whatsapp' ? 'WhatsApp sent successfully' : t('deliveryOrdersModule.actions.sendChallanEmailSuccess'));
                                if (onRefresh) onRefresh();
                            } else {
                                showError(res?.error || t('deliveryOrdersModule.actions.sendChallanEmailFailed'));
                            }
                        } catch {
                            showError(t('deliveryOrdersModule.actions.sendChallanEmailFailed'));
                        } finally {
                            setActionLoading(false);
                            setSendChallanEmailTargetId(null);
                        }
                    }
                }}
            />
        </>
    );
};

export default DeliveryOrdersTable;
